import React, { useState, useEffect, useRef } from 'react';
import { db } from '../firebase';
import { doc, setDoc } from 'firebase/firestore';
import { useTheme } from '../ThemeContext';

const F = "'Fira Code', monospace";

const Salary = ({ data, onUpdate, user }) => {
  const { T } = useTheme();
  const [baseSalary, setBaseSalary] = useState('');
  const [pfAmount, setPfAmount] = useState('');
  const [bonusAmount, setBonusAmount] = useState('');
  const [bonusNote, setBonusNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState({ type: null, idx: null });
  const listRef = useRef(null);

  const showFlash = (msg) => { setFlash(msg); setTimeout(() => setFlash(''), 2500); };

  const handleSetSalary = async () => {
    const base = parseFloat(baseSalary); const pf = parseFloat(pfAmount);
    if (!base || base <= 0) return showFlash('✗ ENTER VALID SALARY');
    if (!pf || pf < 0) return showFlash('✗ ENTER VALID PF AMOUNT');
    if (pf >= base) return showFlash('✗ PF CANNOT EXCEED SALARY');
    setSaving(true);
    const balance = base - pf;
    const newPfTotal = (data.pfTotal || 0) + pf;
    const entry = { baseSalary: base, pfAmount: pf, balance, date: new Date().toISOString() };
    const history = [...(data.salaryHistory || []), entry];
    try {
      await setDoc(doc(db, 'users', user.uid, 'data', 'salary'), {
        baseSalary: base, pfAmount: pf, currentBalance: balance,
        pfTotal: newPfTotal, salaryHistory: history, bonuses: data.bonuses || [],
      }, { merge: true });
      onUpdate({ salaryBalance: balance, pfTotal: newPfTotal, salaryHistory: history, baseSalary: base, pfAmountSet: pf, bonuses: data.bonuses || [] });
      setBaseSalary(''); setPfAmount('');
      showFlash('✓ SALARY PROTOCOL UPDATED');
    } catch (e) { showFlash('✗ FIREBASE WRITE FAILED'); }
    setSaving(false);
  };

  const handleAddBonus = async () => {
    const amt = parseFloat(bonusAmount);
    if (!amt || amt <= 0) return showFlash('✗ ENTER VALID BONUS AMOUNT');
    setSaving(true);
    const newBalance = (data.salaryBalance || 0) + amt;
    const bonusEntry = { amount: amt, note: bonusNote || 'BONUS', date: new Date().toISOString() };
    const bonuses = [...(data.bonuses || []), bonusEntry];
    try {
      await setDoc(doc(db, 'users', user.uid, 'data', 'salary'), { currentBalance: newBalance, bonuses }, { merge: true });
      onUpdate({ salaryBalance: newBalance, bonuses });
      setBonusAmount(''); setBonusNote('');
      showFlash('✓ BONUS CREDITED');
    } catch (e) { showFlash('✗ FIREBASE WRITE FAILED'); }
    setSaving(false);
  };

  const handleDelete = async (type, idx) => {
    if (type === 'salary') {
      const realIdx = (data.salaryHistory || []).length - 1 - idx;
      const history = [...(data.salaryHistory || [])];
      const deletedEntry = history.splice(realIdx, 1)[0];
      if (!deletedEntry) return;
      const newPfTotal = Math.max((data.pfTotal || 0) - (deletedEntry.pfAmount || 0), 0);
      // Recalculate balance & baseSalary from last remaining entry
      const lastEntry = history.length > 0 ? history[history.length - 1] : null;
      const newBalance = lastEntry ? lastEntry.balance : 0;
      const newBaseSalary = lastEntry ? lastEntry.baseSalary : 0;
      const newPfAmount = lastEntry ? lastEntry.pfAmount : 0;
      try {
        await setDoc(doc(db, 'users', user.uid, 'data', 'salary'), {
          salaryHistory: history, pfTotal: newPfTotal,
          currentBalance: newBalance, baseSalary: newBaseSalary, pfAmount: newPfAmount,
        }, { merge: true });
        onUpdate({ salaryHistory: history, pfTotal: newPfTotal, salaryBalance: newBalance, baseSalary: newBaseSalary, pfAmountSet: newPfAmount });
        showFlash('✓ SALARY RECORD DELETED');
      } catch (e) { showFlash('✗ DELETE FAILED'); }
    } else {
      const realIdx = (data.bonuses || []).length - 1 - idx;
      const bonuses = [...(data.bonuses || [])];
      const deletedEntry = bonuses.splice(realIdx, 1)[0];
      if (!deletedEntry) return;
      const newBalance = (data.salaryBalance || 0) - (deletedEntry.amount || 0);
      try {
        await setDoc(doc(db, 'users', user.uid, 'data', 'salary'), { bonuses, currentBalance: newBalance }, { merge: true });
        onUpdate({ bonuses, salaryBalance: newBalance });
        showFlash('✓ BONUS DELETED');
      } catch (e) { showFlash('✗ DELETE FAILED'); }
    }
    setDeleteConfirm({ type: null, idx: null });
  };

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' });
  }, [data.salaryHistory, data.bonuses]);

  const historyBars = (data.salaryHistory || []).slice(-10);
  const maxBar = Math.max(...historyBars.map(h => h.balance || 0), 1);

  const cardS = { background: T.card, border: `1px solid ${T.border}`, borderRadius: 10, padding: '22px 22px', marginBottom: 14, boxShadow: T.isDark ? 'none' : '0 2px 12px rgba(0,0,0,0.06)', transition: 'background .3s,border .3s' };
  const hdrS  = { fontSize: 10, letterSpacing: '0.25em', color: T.text, fontFamily: F, marginBottom: 18, fontWeight: 700, paddingBottom: 10, borderBottom: `1px solid ${T.border}` };
  const logS  = { padding: '12px 10px', borderBottom: `1px solid ${T.border}`, fontFamily: F };

  return (
    <div style={{ maxWidth: 760, margin: '0 auto' }}>
      <Flash msg={flash} T={T} />

      {/* Status Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
        <StatCard label="SALARY BALANCE" value={`₹${(data.salaryBalance || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`} color={T.sky} T={T} />
        <StatCard label="PF SAVINGS"     value={`₹${(data.pfTotal || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`}       color={T.violet} T={T} />
        <StatCard label="TOTAL BONUSES"  value={`₹${(data.bonuses || []).reduce((s, b) => s + b.amount, 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`} color={T.amber} T={T} />
      </div>

      {/* Bar Chart */}
      {historyBars.length > 0 && (
        <div style={cardS}>
          <div style={hdrS}>📊 SALARY HISTORY</div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 100, padding: '0 8px' }}>
            {historyBars.map((h, i) => (
              <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                <span style={{ fontSize: 7, color: T.dim }}>₹{(h.balance/1000).toFixed(0)}k</span>
                <div style={{ width: '100%', maxWidth: 32, minHeight: 4, height: `${(h.balance / maxBar) * 80}%`, background: `linear-gradient(180deg, ${T.sky}, ${T.sky}33)`, borderRadius: '4px 4px 0 0', transition: 'height 0.5s ease' }} />
                <span style={{ fontSize: 7, color: T.muted }}>{new Date(h.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }).split(' ')[0]}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Set Salary */}
      <div style={cardS}>
        <div style={hdrS}>₹ SET MONTHLY SALARY</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 18 }}>
          <InputBlock label="BASE SALARY (₹)" type="number" value={baseSalary} onChange={setBaseSalary} onEnter={handleSetSalary} T={T} />
          <InputBlock label="PF DEDUCTION (₹)" type="number" value={pfAmount} onChange={setPfAmount} onEnter={handleSetSalary} T={T} />
        </div>
        <Btn onClick={handleSetSalary} disabled={saving} label={saving ? 'PROCESSING...' : 'DEPLOY SALARY'} color={T.sky} />
      </div>

      {/* Add Bonus */}
      <div style={cardS}>
        <div style={hdrS}>⚡ ADD BONUS / INCENTIVE</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.5fr', gap: 14, marginBottom: 18 }}>
          <InputBlock label="AMOUNT (₹)" type="number" value={bonusAmount} onChange={setBonusAmount} onEnter={handleAddBonus} T={T} />
          <InputBlock label="NOTE" type="text" value={bonusNote} onChange={setBonusNote} onEnter={handleAddBonus} placeholder="Performance Bonus" T={T} />
        </div>
        <Btn onClick={handleAddBonus} disabled={saving} label={saving ? 'PROCESSING...' : 'CREDIT BONUS'} color={T.amber} />
      </div>

      {/* Transaction Log */}
      <div style={cardS}>
        <div style={hdrS}>📋 TRANSACTION LOG</div>
        <div ref={listRef} style={{ maxHeight: 240, overflowY: 'auto', scrollbarWidth: 'thin', scrollbarColor: `${T.scrollC} transparent` }}>
          {(data.salaryHistory || []).length === 0 && (data.bonuses || []).length === 0 && (
            <div style={{ textAlign: 'center', color: T.dim, fontSize: 10, padding: 30, letterSpacing: '0.2em' }}>NO RECORDS FOUND</div>
          )}
          {[...(data.salaryHistory || [])].reverse().map((e, i) => (
            <div key={`s-${i}`} style={logS}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: T.sky, fontSize: 10 }}>SALARY SET</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ color: T.dim, fontSize: 8 }}>{new Date(e.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                  {deleteConfirm.type === 'salary' && deleteConfirm.idx === i ? (
                    <span onClick={() => handleDelete('salary', i)} style={{ color: T.rose, cursor: 'pointer', fontSize: 8, fontWeight: 900 }}>OK?</span>
                  ) : (
                    <span onClick={() => setDeleteConfirm({ type: 'salary', idx: i })} style={{ color: T.muted, cursor: 'pointer', fontSize: 11, transition: 'color 0.2s' }}
                      onMouseOver={ev => ev.currentTarget.style.color = T.rose} onMouseOut={ev => ev.currentTarget.style.color = T.muted}>✕</span>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 16, marginTop: 5, fontSize: 9, color: T.dim }}>
                <span>BASE: ₹{e.baseSalary?.toLocaleString('en-IN')}</span>
                <span>PF: ₹{e.pfAmount?.toLocaleString('en-IN')}</span>
                <span style={{ color: T.emerald }}>NET: ₹{e.balance?.toLocaleString('en-IN')}</span>
              </div>
            </div>
          ))}
          {[...(data.bonuses || [])].reverse().map((e, i) => (
            <div key={`b-${i}`} style={logS}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: T.amber, fontSize: 10 }}>⚡ BONUS</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ color: T.dim, fontSize: 8 }}>{new Date(e.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                  {deleteConfirm.type === 'bonus' && deleteConfirm.idx === i ? (
                    <span onClick={() => handleDelete('bonus', i)} style={{ color: T.rose, cursor: 'pointer', fontSize: 8, fontWeight: 900 }}>OK?</span>
                  ) : (
                    <span onClick={() => setDeleteConfirm({ type: 'bonus', idx: i })} style={{ color: T.muted, cursor: 'pointer', fontSize: 11, transition: 'color 0.2s' }}
                      onMouseOver={ev => ev.currentTarget.style.color = T.rose} onMouseOut={ev => ev.currentTarget.style.color = T.muted}>✕</span>
                  )}
                </div>
              </div>
              <div style={{ marginTop: 5, fontSize: 9 }}>
                <span style={{ color: T.amber }}>+₹{e.amount?.toLocaleString('en-IN')}</span>
                <span style={{ color: T.dim, marginLeft: 12 }}>{e.note}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ── Shared Components ── */
const Flash = ({ msg, T }) => msg ? (
  <div style={{
    position: 'fixed', top: 20, left: '50%', transform: 'translateX(-50%)', zIndex: 999,
    background: msg.startsWith('✓') ? `${T?.emerald || '#34d399'}18` : `${T?.rose || '#fb7185'}18`,
    border: `1px solid ${msg.startsWith('✓') ? (T?.emerald || '#34d399') : (T?.rose || '#fb7185')}`,
    color: msg.startsWith('✓') ? (T?.emerald || '#34d399') : (T?.rose || '#fb7185'),
    padding: '10px 28px', borderRadius: 6, fontSize: 11, letterSpacing: '0.2em',
    fontFamily: F, animation: 'flashIn 0.3s ease',
    boxShadow: `0 4px 20px ${msg.startsWith('✓') ? 'rgba(52,211,153,0.15)' : 'rgba(251,113,133,0.15)'}`,
  }}>
    {msg}
    <style>{`@keyframes flashIn { from{opacity:0;transform:translateX(-50%) translateY(-8px)} to{opacity:1;transform:translateX(-50%) translateY(0)} }`}</style>
  </div>
) : null;

const StatCard = ({ label, value, color, T }) => (
  <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 10, padding: '18px 16px', textAlign: 'center', position: 'relative', overflow: 'hidden', boxShadow: T.isDark ? 'none' : '0 2px 10px rgba(0,0,0,0.05)', transition: 'background .3s' }}>
    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: color, opacity: 0.5 }} />
    <div style={{ fontSize: 7, letterSpacing: '0.35em', color: T.dim, fontFamily: F, marginBottom: 10 }}>{label}</div>
    <div style={{ fontSize: 18, fontWeight: 900, color, fontFamily: F, letterSpacing: '0.02em' }}>{value}</div>
  </div>
);

const InputBlock = ({ label, type, value, onChange, onEnter, placeholder, T }) => (
  <div>
    <label style={{ fontSize: 8, letterSpacing: '0.35em', color: T.dim, fontFamily: F, marginBottom: 6, display: 'block' }}>&gt; {label}</label>
    <input type={type} value={value} onChange={e => onChange(e.target.value)}
      onKeyDown={e => e.key === 'Enter' && onEnter?.()}
      placeholder={placeholder || '0.00'}
      style={{ width: '100%', background: T.inputBg, border: 'none', borderBottom: `1.5px solid ${T.inputBdr}`, padding: '10px 8px', color: T.text, fontSize: 14, letterSpacing: '0.1em', outline: 'none', fontFamily: F, caretColor: T.inputFocus, transition: 'border-color 0.2s' }}
      onFocus={e => e.target.style.borderBottomColor = T.inputFocus}
      onBlur={e => e.target.style.borderBottomColor = T.inputBdr}
    />
  </div>
);

const Btn = ({ onClick, disabled, label, color }) => (
  <button onClick={onClick} disabled={disabled}
    style={{ width: '100%', background: 'transparent', border: `1px solid ${color}44`, color, padding: '12px 0', fontSize: 10, letterSpacing: '0.5em', fontWeight: 700, cursor: 'pointer', fontFamily: F, borderRadius: 6, transition: 'all 0.25s' }}
    onMouseOver={e => { e.currentTarget.style.background = color + '15'; e.currentTarget.style.borderColor = color; }}
    onMouseOut={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = color + '44'; }}
  >{label}</button>
);

export default Salary;
